// generic/base event tracking functions.
// specific implementations that wrap these with domain-specific logic and parameters should go in snowplow/tracking.ts.

import { createSnowplowTracker, getSnowplowTracker, HudlDataSchemas } from '@hudl/analytics';
import * as Sentry from '@sentry/react';
import { KeysToCamelCase } from '@statsbomb/parachute-types';
import { memoize } from 'lodash/fp';
import env from '@/env';
import { Nullable } from '@/types/generic';
import { pause } from '@/utils/timing';
import {
  ObjectConfigurationEventParams,
  ViewConfiguration,
  ViewConfigurationSource,
  ViewSubType,
  ViewType,
} from './types';

export const initialSentrySetup = (userId: string, email: Nullable<string | undefined>) => {
  Sentry.setUser({ email: email ?? undefined, id: userId });
};

export const initialiseSnowplow = memoize(
  async (
    hudlUserId: string,
    entityId: string,
    entityName: string,
    isInternal: boolean,
    pageUrl: string,
    pageType: string,
  ) => {
    createSnowplowTracker({
      id: 'parachute',
      platform: 'web',
      env: env.VITE_NODE_ENV === 'production' ? 'prod' : 'thor',
      userID: hudlUserId,
    });

    const applicationEntity = HudlDataSchemas.ApplicationEntity.V1_0_0.describe({ app_name: 'HudlParachute' });
    const userOrgEntity = HudlDataSchemas.UserOrgEntity.V1_0_0.describe({ org_id: entityId, org_name: entityName });
    const userEntity = HudlDataSchemas.UserEntity.V4_0_3.describe({ user_id: hudlUserId, is_internal: isInternal });
    const pageEntity = HudlDataSchemas.PageEntity.V2_0_2.describe({
      page_url: pageUrl,
      page_type: pageType || 'unknown', // fallback to 'unknown' if not set
    });

    // this automatically sends app context in every request
    getSnowplowTracker('parachute').addGlobalContexts('parachute', [
      applicationEntity,
      userOrgEntity,
      userEntity,
      pageEntity,
    ]);
  },
);

const trackEvent = async (
  event: HudlDataSchemas.SelfDescribingEvent,
  contexts?: HudlDataSchemas.SelfDescribingEntity[],
) => {
  if (env.VITE_NODE_ENV !== 'development') getSnowplowTracker('parachute').trackSelfDescribingEvent(event, contexts);
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/view_configuration_event/jsonschema/1-0-0
export const trackViewConfigurationEvent = async ({
  previousConfiguration,
  updatedConfiguration,
  viewType,
  viewSubType,
  configurationSource,
}: {
  previousConfiguration: ViewConfiguration;
  updatedConfiguration: ViewConfiguration;
  viewType: ViewType;
  viewSubType?: ViewSubType;
  configurationSource: ViewConfigurationSource;
}) => {
  // Annoyingly, like all the @hudl/analytics functions, this returns a promise, but it is instantly resolved - awaiting
  // it does not mean the event has been sent. I'm returning a short delay promise here to give the event time to be
  // sent before the user is navigated away from the application, otherwise it can be cancelled which screws up the
  // tracking.
  trackEvent(
    HudlDataSchemas.ViewConfigurationEvent.V1_0_0.describe({
      previous_configuration: previousConfiguration,
      updated_configuration: updatedConfiguration,
      view_type: viewType,
      configuration_source: configurationSource,
      view_sub_type: viewSubType,
    }),
  );
  // I've gone with 400ms here because the slowest response time I've seen from the snowplow server is 320ms, this gives
  // an 80ms buffer and isn't _too_ noticeable for the user.
  return pause(400);
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/content_playback_event/jsonschema/3-0-5
export const trackContentPlaybackEvent = async ({
  contentType,
  playbackActionType,
  startTimeMs,
  endTimeMs,
  playbackRate,
  angleName,
  angleCount,
  context,
}: KeysToCamelCase<HudlDataSchemas.ContentPlaybackEvent.V3_0_5.Data>) => {
  trackEvent(
    HudlDataSchemas.ContentPlaybackEvent.V3_0_5.describe({
      content_type: contentType,
      playback_action_type: playbackActionType,
      start_time_ms: startTimeMs,
      end_time_ms: endTimeMs,
      playback_rate: playbackRate,
      angle_name: angleName,
      angle_count: angleCount,
      context,
    }),
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_open_close_event/jsonschema/1-0-13
export const trackObjectOpenCloseEvent = async ({
  objectType,
  openVsClose,
  conditionType,
}: KeysToCamelCase<HudlDataSchemas.ObjectOpenCloseEvent.V1_0_13.Data> & { conditionType?: string }) => {
  const conditionEntity: HudlDataSchemas.SelfDescribingEntity[] = conditionType
    ? [HudlDataSchemas.ConditionEntity.V2_0_0.describe({ condition_type: conditionType })]
    : [];
  const contexts = [...conditionEntity];

  trackEvent(
    HudlDataSchemas.ObjectOpenCloseEvent.V1_0_13.describe({
      object_type: objectType,
      open_vs_close: openVsClose,
    }),
    contexts,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/content_filter_event/jsonschema/2-0-2
export const trackContentFilterEvent = async ({
  filterType,
  filterSource,
  appliedVsRemoved,
  fromSavedFilters,
  filterSelection,
}: KeysToCamelCase<HudlDataSchemas.ContentFilterEvent.V2_0_2.Data>) =>
  trackEvent(
    HudlDataSchemas.ContentFilterEvent.V2_0_2.describe({
      filter_type: filterType,
      filter_source: filterSource,
      applied_vs_removed: appliedVsRemoved,
      from_saved_filters: fromSavedFilters,
      filter_selection: filterSelection,
    }),
  );

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_creation_event/jsonschema/1-0-14
export const trackObjectCreationEvent = async ({
  objectType,
  context,
  name,
}: KeysToCamelCase<HudlDataSchemas.ObjectCreationEvent.V1_0_14.Data>) =>
  trackEvent(
    HudlDataSchemas.ObjectCreationEvent.V1_0_14.describe({
      object_type: objectType,
      context,
      name,
    }),
  );

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_change_event/jsonschema/1-0-16
export const trackObjectChangeEvent = async ({
  objectType,
  changeType,
  context,
}: KeysToCamelCase<HudlDataSchemas.ObjectChangeEvent.V1_0_16.Data>) =>
  trackEvent(
    HudlDataSchemas.ObjectChangeEvent.V1_0_16.describe({
      object_type: objectType,
      change_type: changeType,
      context,
    }),
  );

// https://console.snowplowanalytics.com/39c16d82-da86-44be-ad37-c7024a7ce5dd/data-structures/5a9432fc0b449be4371481fe5f6ea7570df3ce2ad08e42215c461aef9341227b
export const trackObjectConfigurationEvent = async ({
  objectType,
  objectSubType,
  configurationType,
  previousConfiguration,
  updatedConfiguration,
  configurationSource,
}: ObjectConfigurationEventParams) =>
  trackEvent(
    HudlDataSchemas.ObjectConfigurationEvent.V1_0_0.describe({
      object_type: objectType,
      object_sub_type: objectSubType,
      configuration_type: configurationType,
      previous_configuration: previousConfiguration,
      updated_configuration: updatedConfiguration,
      configuration_source: configurationSource,
    }),
  );

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/general_ui_interaction/jsonschema/1-0-0
export const trackGeneralUiInteractionEvent = async ({
  interactionType,
  itemLocation,
  itemName,
  itemType,
}: KeysToCamelCase<HudlDataSchemas.GeneralUiInteraction.V1_0_0.Data>) =>
  trackEvent(
    HudlDataSchemas.GeneralUiInteraction.V1_0_0.describe({
      interaction_type: interactionType,
      item_location: itemLocation,
      item_name: itemName,
      item_type: itemType,
    }),
  );

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/content_upload_download_event/jsonschema/1-0-6
export const trackContentUploadDownloadEvent = async ({
  uploadVsDownload,
  contentType,
  contentSubType,
  context,
}: KeysToCamelCase<HudlDataSchemas.ContentUploadDownload.V1_0_6.Data>) =>
  trackEvent(
    HudlDataSchemas.ContentUploadDownload.V1_0_6.describe({
      upload_vs_download: uploadVsDownload,
      content_type: contentType,
      content_sub_type: contentSubType,
      context,
    }),
  );
